import React, { useEffect, useState } from "react";
// Accounts tab of the admin page: list every account, change roles, delete.

import { Container, Table, Form, Button, Spinner } from "react-bootstrap";
import { common } from "../../js/common";

interface Account {
  id: number;
  userName: string;
  email: string;
  role: string;
  createdOn: string;
}

function AccountsTab() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadAccounts = async () => {
    const res = await common.fetchRestAPI("accounts", "GET");
    if (!res.ok) throw new Error("Failed to load accounts");
    const body = await res.json();
    setAccounts(body.accounts ?? []);
  };

  useEffect(() => {
    (async () => {
      try {
        const sessionRes = await common.fetchRestAPI("sessions", "GET");
        if (sessionRes.ok) {
          const session = await sessionRes.json();
          setCurrentUserId(session.id);
        }
        await loadAccounts();
      } catch (err: any) {
        setError(err.message || "Failed to load accounts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const changeRole = async (account: Account, role: string) => {
    if (role === account.role) return;
    setError("");
    setNotice("");
    setBusyId(account.id);
    try {
      const res = await common.fetchRestAPI(`accounts/${account.id}`, "PUT", { role });
      if (!res.ok) throw new Error(`Failed to update role for ${account.userName}`);
      setAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, role } : a)));
      setNotice(`${account.userName} is now ${role}.`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteAccount = async (account: Account) => {
    if (!window.confirm(`Delete account "${account.userName}"? This also deletes their recipes and cannot be undone.`)) return;
    setError("");
    setNotice("");
    setBusyId(account.id);
    try {
      const res = await common.fetchRestAPI(`accounts/${account.id}`, "DELETE");
      if (!res.ok) throw new Error(`Failed to delete ${account.userName}`);
      setAccounts((prev) => prev.filter((a) => a.id !== account.id));
      setNotice(`Deleted ${account.userName}.`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center p-5">
        <Spinner animation="border" size="sm" role="status" />
      </Container>
    );
  }

  return (
    <div>
      <h2 className="h4">Accounts</h2>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError("")} aria-label="Close"></button>
        </div>
      )}
      {notice && <div className="alert alert-success" role="alert">{notice}</div>}
      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>ID</th>
            <th>Username</th>
            <th>Email</th>
            <th>Role</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account) => {
            const isSelf = account.id === currentUserId;
            const busy = busyId === account.id;
            return (
              <tr key={account.id}>
                <td>{account.id}</td>
                <td>{account.userName}{isSelf && <span className="text-muted"> (you)</span>}</td>
                <td>{account.email}</td>
                <td>
                  <Form.Select
                    size="sm"
                    value={account.role}
                    disabled={busy || isSelf}
                    aria-label={`Role for ${account.userName}`}
                    onChange={(e) => changeRole(account, e.target.value)}
                  >
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </Form.Select>
                </td>
                <td>{new Date(account.createdOn).toLocaleDateString()}</td>
                <td className="text-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={busy || isSelf}
                    onClick={() => deleteAccount(account)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {accounts.length === 0 && <p>No accounts found.</p>}
    </div>
  );
}

export default AccountsTab;
